import { Component, inject, OnInit, signal } from '@angular/core';
import { Footer } from '../../../core/shell/footer/footer';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { AuthService } from '../../services/auth.service';

import { AuthServerErrorService } from '../../services/auth-server-error.service';
import { mustMatch } from '../../../core/validators/must-match.validator';
import { finalize } from 'rxjs';
import { InputFieldComponent } from '../../../core/components/inputs/input-field/input-field.component';
import { PasswordComponent } from '../../../core/components/inputs/password/password.component';
import { passwordValidator } from '../../../core/validators/password-validator';

@Component({
    selector: 'app-reset-password',
    imports: [
    Footer,
    ReactiveFormsModule,
    InputFieldComponent,
    PasswordComponent
],
    templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly authServerErrorService = inject(AuthServerErrorService);

    private readonly token = signal<string | null>(null);
    public readonly errorMessage = signal<string>('');
    public form!: FormGroup;

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            this.token.set(params['token']);

            if (!this.token()) {
                this.router.navigate(['/']);
            }

            this.initForm();
        });
    }

    private initForm(): void {
        this.form = this.fb.nonNullable.group({
            email: this.fb.control('', [ Validators.required, Validators.email ]),
            password: this.fb.control('', [ Validators.required, passwordValidator() ]),
            passwordConfirmation: this.fb.control('', [ Validators.required ])
        }, {
            validators: [mustMatch('password', 'passwordConfirmation')]
        });
    }

    public onSubmit(): void {
        if (this.form.valid && this.token()) {
            this.form.markAsPending();

            this.authService.resetPassword(
                this.token()!,
                this.form.value.email,
                this.form.value.password,
                this.form.value.passwordConfirmation
            ).pipe(
                finalize(() => {
                    this.form.setErrors({});
                })
            )
            .subscribe({
                error: (error) => {
                    const errorMessage = this.authServerErrorService.getErrorMessage(error);

                    this.errorMessage.set(errorMessage);
                },
                complete: () => {
                    this.router.navigate(['/']);
                }
            })
        }
    }

    public onCancel(): void {
        this.router.navigate(['/']);
    }

    public getFormField(field: string): FormControl {
        return this.form.get(field) as FormControl;
    }
}
